import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRY_CODES, getFlagUrl } from "@/lib/countryCodes";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
};

const CountryCodePicker = ({ value, onValueChange, className, style }: Props) => {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => COUNTRY_CODES.find((c) => c.code === value),
    [value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[130px] shrink-0 justify-between px-3 bg-background", className)}
          style={style}
        >
          <span className="flex items-center gap-1.5 truncate">
            {selected && (
              <img src={getFlagUrl(selected.iso)} alt="" className="w-5 h-3.5 object-cover rounded-sm" />
            )}
            {selected ? selected.code : "+..."}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRY_CODES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.country} ${c.code}`}
                  onSelect={() => {
                    onValueChange(c.code);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === c.code ? "opacity-100" : "opacity-0")} />
                  <img src={getFlagUrl(c.iso)} alt="" className="w-5 h-3.5 object-cover rounded-sm mr-2 shrink-0" />
                  <span className="flex-1 truncate">{c.country}</span>
                  <span className="text-muted-foreground text-sm ml-1">{c.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CountryCodePicker;
